import React, { useCallback, useMemo, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { useDispatch } from 'react-redux';

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faBarcode } from '@fortawesome/pro-solid-svg-icons/faBarcode';
import { faEllipsisH } from '@fortawesome/pro-solid-svg-icons/faEllipsisH';
import { faPen } from '@fortawesome/pro-regular-svg-icons/faPen';
import { faPlus } from '@fortawesome/pro-solid-svg-icons/faPlus';
import { faTrash } from '@fortawesome/pro-solid-svg-icons/faTrash';

import { Button } from 'ui-components/components/button';
import { THEME_CONSTANTS } from 'ui-components/services/button-theme-service';
import { DropdownMenu } from 'ui-components/components/dropdown-menu';
import { DropdownMenuItem } from 'ui-components/components/dropdown-menu-item';
import { DropdownMenuToggler } from 'ui-components/components/dropdown-menu-toggler';
import { FlexRow } from 'ui-components/components/flex';
import { LoadingIndicator } from 'ui-components/components/loading-indicator';
import { NotificationService, NOTIFICATION_TYPE_ERROR, NOTIFICATION_TYPE_SUCCESS } from 'ui-components/services/notification-service';
import { Text, TextType } from 'ui-components/components/text';
import { WeightInputUnit } from 'ui-components/components/weight-input/units';

import { printUomBarcodes } from '../../../../../store/printing/print.actions';
import { useSendMetrics } from '../../../../../hooks/use-send-metrics';
import {
  INVENTORY_UOM_UNIT_SAVED,
  INVENTORY_UOM_BARCODES_PRINTED,
  INVENTORY_UOM_UNIT_DELETED,
  INVENTORY_UOM_DELETE_BLOCKED,
} from '../../../../../constants/analytics-events';
import { getKilogramsAndGramsFromGrams } from '../../../../../ui-components/components/weight-input/helpers';
import { getPoundsAndOuncesFromOunces } from '../../../../../ui-components/components/weight-input/ounces-weight-input';
import { SimpleModal } from '../../../../../components/simple-modal';
import { useProductDetailsContext } from '../../../use-product-details-context';

import { DeleteBlockedContent, type DeleteBlockedInfo } from './components/delete-blocked-content/delete-blocked-content';
import { UomModal } from './components/uom-modal/uom-modal';
import { messages } from './messages';
import { useIsUnitsOfMeasureAvailable } from '../../../../../feature-gates/use-is-units-of-measure-available';
import { UomUpsellEmptyState } from './components/uom-upsell-empty-state/uom-upsell-empty-state';
import style from './uom.module.less';
import { useProductUom } from './use-product-uom';
import type { ProductUnitOfMeasureResponse, UomFormState, PersistPayload } from './uom-types';

const sortById = (a: ProductUnitOfMeasureResponse, b: ProductUnitOfMeasureResponse): number =>
  a.productUnitOfMeasureId - b.productUnitOfMeasureId;

const buildConversionSummary = (items: ProductUnitOfMeasureResponse[]): string[] => {
  if (items.length < 2) return [];

  const byId = new Map(items.map((i) => [i.productUnitOfMeasureId, i]));
  const nonBase = items.filter((i) => i.productUnitOfMeasureId !== 0).sort(sortById);

  return nonBase.map((row) => {
    const parts: string[] = [`1 ${row.uomLevel}`];
    let current: ProductUnitOfMeasureResponse | undefined = row;

    while (current?.parentProductUnitOfMeasureId != null) {
      const parent = byId.get(current.parentProductUnitOfMeasureId);
      if (!parent) break;
      const parentName = parent.uomLevel || parent.uomTypeName;
      parts.push(`${row.eaches / (parent.eaches || 1)} x ${parentName}`);
      current = parent;
    }

    if (row.eaches > 1) {
      parts.push(`${row.eaches} x Each`);
    }

    return parts.join(' = ');
  });
};

type UnitInfo = { id: number; abbr: string };

const DIMENSION_UNITS: UnitInfo[] = [
  { id: 3, abbr: 'in' },
  { id: 4, abbr: 'cm' },
  { id: 7, abbr: 'in' },
  { id: 8, abbr: 'cm' },
];

const WEIGHT_UNITS: UnitInfo[] = [
  { id: 1, abbr: 'oz' },
  { id: 2, abbr: 'g' },
  { id: 5, abbr: 'lbs' },
  { id: 6, abbr: 'kg' },
];

const DIMENSION_UNIT_BY_NAME: Record<string, UnitInfo> = { inches: DIMENSION_UNITS[0], centimeters: DIMENSION_UNITS[1] };
const WEIGHT_UNIT_BY_NAME: Record<string, UnitInfo> = {
  ounces: WEIGHT_UNITS[0],
  grams: WEIGHT_UNITS[1],
  pounds: WEIGHT_UNITS[2],
  kilograms: WEIGHT_UNITS[3],
};

const DIMENSION_ABBR_BY_ID = new Map(DIMENSION_UNITS.map((u) => [u.id, u.abbr]));
const WEIGHT_ABBR_BY_ID = new Map(WEIGHT_UNITS.map((u) => [u.id, u.abbr]));

const toWeightInputUnit = (unit: string | undefined): 'ounces' | 'grams' | undefined => {
  if (unit === WeightInputUnit.grams) return 'grams';
  if (unit === WeightInputUnit.ounces) return 'ounces';
  return undefined;
};

const SYNTHETIC_BASE_ROW_ID = -1;

// Matches ApiErrorCode.UnitOfMeasureReferencedByOpenOrder returned by the WebV3Api when a UoM
// cannot be edited or deleted because it is referenced by an open purchase or transfer order.
const UOM_REFERENCED_BY_OPEN_ORDER_CODE = 'UnitOfMeasureReferencedByOpenOrder';
// Matches SS.Core UnitOfMeasureBlockingOrderType, surfaced as ErrorContext.OrderType on the error.
const TRANSFER_ORDER_BLOCKING_TYPE = 'TransferOrder';

type UomInUseError = { Message?: string; Code?: string; ErrorContext?: { OrderType?: string } };

// Extracts the first API error when the failure is a UoM-blocked-by-open-order error, else null.
const getOpenOrderBlockError = (err: unknown): UomInUseError | null => {
  const serverError = (err as { response?: { data?: { Errors?: UomInUseError[] } } })?.response?.data?.Errors?.[0];
  return serverError?.Code === UOM_REFERENCED_BY_OPEN_ORDER_CODE ? serverError : null;
};

export const ProductDetailsUnitOfMeasureV3 = () => {
  const { isPlanGated } = useIsUnitsOfMeasureAvailable();

  if (isPlanGated) return <UomUpsellEmptyState />;

  return <ProductDetailsUnitOfMeasureV3Content />;
};

const ProductDetailsUnitOfMeasureV3Content = () => {
  const intl = useIntl();
  const dispatch = useDispatch();
  const trackMetrics = useSendMetrics();
  const { productId, productSku, weightUnit, sizeUnit } = useProductDetailsContext();
  const { items, uomTypes, isLoading, hasError, create, update, remove } = useProductUom(productId);

  const dimensionUnit = DIMENSION_UNIT_BY_NAME[(sizeUnit ?? '').toLowerCase()];
  const weightUnitInfo = WEIGHT_UNIT_BY_NAME[(weightUnit ?? '').toLowerCase()];

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingRow, setEditingRow] = useState<ProductUnitOfMeasureResponse | null>(null);
  const [deletingRow, setDeletingRow] = useState<ProductUnitOfMeasureResponse | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const [deleteBlockedInfo, setDeleteBlockedInfo] = useState<DeleteBlockedInfo | null>(null);

  const baseRow = useMemo(() => items.find((i) => i.productUnitOfMeasureId === 0), [items]);
  const nonBaseItems = useMemo(() => items.filter((i) => i.productUnitOfMeasureId !== 0), [items]);
  const orderedNonBase = useMemo(() => [...nonBaseItems].sort(sortById), [nonBaseItems]);

  const allItemsById = useMemo(() => new Map(items.map((i) => [i.productUnitOfMeasureId, i])), [items]);

  const parentForRow = (row: ProductUnitOfMeasureResponse): ProductUnitOfMeasureResponse | undefined => {
    if (row.parentProductUnitOfMeasureId == null) return baseRow;
    return allItemsById.get(row.parentProductUnitOfMeasureId);
  };

  const qtyUnitNameForParent = (parent: ProductUnitOfMeasureResponse | undefined): string => {
    if (!parent) return '';
    return parent.uomLevel || parent.uomTypeName || '';
  };

  const conversionSummary = useMemo(() => buildConversionSummary(items), [items]);

  const openAddModal = useCallback(() => {
    setEditingRow(null);
    setIsModalOpen(true);
  }, []);

  const openEditModal = useCallback((row: ProductUnitOfMeasureResponse) => {
    setEditingRow(row);
    setIsModalOpen(true);
  }, []);

  const closeModal = useCallback(() => {
    setIsModalOpen(false);
    setEditingRow(null);
  }, []);

  const handleModalSave = useCallback(
    async (form: UomFormState) => {
      const typeName = uomTypes.find((t) => t.uomTypeId === form.uomTypeId)?.name ?? '';

      const parentId = form.containsId !== SYNTHETIC_BASE_ROW_ID && form.containsId !== 0 ? form.containsId : null;

      const toNum = (v: string): number | null => {
        const n = Number(v);
        return v.trim() && !Number.isNaN(n) ? n : null;
      };

      const payload: PersistPayload = {
        uomTypeId: form.uomTypeId,
        uomLevel: typeName,
        parentProductUnitOfMeasureId: parentId,
        conversionFactor: Number(form.quantity) || 1,
        uomSku: form.uomSku || null,
        barcode: form.barcode || null,
        length: toNum(form.length),
        width: toNum(form.width),
        height: toNum(form.height),
        weight: toNum(form.weight),
        dimensionUnitId: dimensionUnit?.id ?? null,
        weightUnitId: weightUnitInfo?.id ?? null,
        isStackable: form.isStackable,
        isSold: form.isSold,
        isStocked: form.isStocked,
      };

      try {
        if (editingRow && editingRow.productUnitOfMeasureId != null) {
          await update(editingRow.productUnitOfMeasureId, payload);
        } else {
          await create(payload);
        }
        trackMetrics(INVENTORY_UOM_UNIT_SAVED, {
          productId,
          action: editingRow ? 'edit' : 'add',
          uomTypeName: typeName,
          totalLevelsAfterSave: editingRow ? items.length : items.length + 1,
        });
        closeModal();
        NotificationService.showNotification({
          notificationType: NOTIFICATION_TYPE_SUCCESS,
          content: intl.formatMessage(editingRow ? messages.uomEditSuccess : messages.uomAddSuccess, { typeName }),
        });
      } catch (err) {
        const openOrderBlock = getOpenOrderBlockError(err);
        const serverMessage =
          (err as { response?: { data?: { Errors?: { Message?: string }[] } } })?.response?.data?.Errors?.[0]?.Message ?? '';
        const isBarcodeConflict = serverMessage.includes('already exists');
        let content: string;
        if (openOrderBlock) {
          content = intl.formatMessage(
            openOrderBlock.ErrorContext?.OrderType === TRANSFER_ORDER_BLOCKING_TYPE
              ? messages.uomEditBlockedByOpenTransferOrder
              : messages.uomEditBlockedByOpenPurchaseOrder,
          );
        } else if (isBarcodeConflict) {
          content = intl.formatMessage(messages.uomSaveBarcodeConflict, { barcode: form.barcode });
        } else {
          content = intl.formatMessage(messages.uomSaveError);
        }
        NotificationService.showNotification({
          notificationType: NOTIFICATION_TYPE_ERROR,
          content,
        });
        throw err;
      }
    },
    [uomTypes, editingRow, dimensionUnit, weightUnitInfo, intl, create, update, closeModal, trackMetrics, items, productId],
  );

  const openDeleteConfirm = useCallback((row: ProductUnitOfMeasureResponse) => {
    setDeletingRow(row);
  }, []);

  const closeDeleteConfirm = useCallback(() => {
    setDeletingRow(null);
  }, []);

  const closeDeleteBlocked = useCallback(() => {
    setDeleteBlockedInfo(null);
  }, []);

  const handlePrintBarcodes = useCallback(() => {
    const barcodes = items
      .filter((item) => item.barcode != null)
      .map((item) => ({
        name: item.barcode as string,
        barcodeContent: item.barcode as string,
      }));

    if (!barcodes.length) return;

    trackMetrics(INVENTORY_UOM_BARCODES_PRINTED, {
      productId,
      barcodeCount: barcodes.length,
    });
    dispatch(printUomBarcodes(barcodes));
  }, [items, dispatch, trackMetrics, productId]);

  const handleDelete = useCallback(async () => {
    if (!deletingRow) return;

    const children = items.filter((i) => i.parentProductUnitOfMeasureId === deletingRow.productUnitOfMeasureId);
    if (children.length > 0) {
      setDeletingRow(null);
      setDeleteBlockedInfo({
        row: deletingRow,
        reason: intl.formatMessage(messages.uomDeleteBlockedByChain),
        childNames: children.map((c) => c.uomTypeName || c.uomLevel),
      });
      trackMetrics(INVENTORY_UOM_DELETE_BLOCKED, {
        productId,
        uomTypeName: deletingRow.uomTypeName,
        productUnitOfMeasureId: deletingRow.productUnitOfMeasureId,
        reason: 'has_children',
        childCount: children.length,
      });
      return;
    }

    setIsDeleting(true);
    try {
      await remove(deletingRow.productUnitOfMeasureId);
      trackMetrics(INVENTORY_UOM_UNIT_DELETED, {
        productId,
        uomTypeName: deletingRow.uomTypeName,
        productUnitOfMeasureId: deletingRow.productUnitOfMeasureId,
        totalLevelsAfterDelete: items.length - 1,
      });
      setDeletingRow(null);
    } catch (error: unknown) {
      const openOrderBlock = getOpenOrderBlockError(error);
      let reason: string;
      if (openOrderBlock) {
        // Prefer a translated, PO/TO-specific message over the raw server string.
        reason = intl.formatMessage(
          openOrderBlock.ErrorContext?.OrderType === TRANSFER_ORDER_BLOCKING_TYPE
            ? messages.uomDeleteBlockedByOpenTransferOrder
            : messages.uomDeleteBlockedByOpenPurchaseOrder,
        );
      } else {
        // Other delete failures (e.g. server-side has-children) return a raw string; render it safely.
        const responseData = (error as { response?: { data?: string | { Errors?: { Message?: string }[] } } })?.response?.data;
        const serverMessage = typeof responseData === 'string' ? responseData : responseData?.Errors?.[0]?.Message;
        reason = serverMessage || intl.formatMessage(messages.uomDeleteError);
      }
      setDeletingRow(null);
      setDeleteBlockedInfo({
        row: deletingRow,
        reason,
        childNames: [],
      });
      trackMetrics(INVENTORY_UOM_DELETE_BLOCKED, {
        productId,
        uomTypeName: deletingRow.uomTypeName,
        productUnitOfMeasureId: deletingRow.productUnitOfMeasureId,
        reason: 'server_error',
      });
    } finally {
      setIsDeleting(false);
    }
  }, [deletingRow, items, intl, remove, trackMetrics, productId]);

  return (
    <div className={style['uom-tab']}>
      <div className={style['content-card']}>
        <FlexRow className={style['card-header']}>
          <div>
            <Text element='div' textType={TextType.H4} value={messages.uomConfigurationTitle} />
            <Text className={style['card-subtitle']} element='div' textType={TextType.Caption} value={messages.uomConfigurationSubtitle} />
          </div>
          <Button
            className={style['print-barcodes-btn']}
            disabled={isLoading || items.every((item) => item.barcode == null)}
            onClick={handlePrintBarcodes}
            theme={THEME_CONSTANTS.secondary}
          >
            <FontAwesomeIcon icon={faBarcode} />
            <FormattedMessage {...messages.uomPrintBarcodes} />
          </Button>
        </FlexRow>

        {hasError && <Text element='div' textType={TextType.Paragraph} value={messages.uomLoadingError} />}

        <div className={style['uom-grid']}>
          <div className={style['uom-grid-header']}>
            <Text element='div' textType={TextType.Label} value={messages.uomHeaderType} />
            <Text element='div' textType={TextType.Label} value={messages.uomHeaderQuantity} />
            <Text element='div' textType={TextType.Label} value={messages.uomHeaderBarcode} />
            <Text element='div' textType={TextType.Label} value={messages.uomHeaderUomSku} />
            <Text element='div' textType={TextType.Label} value={messages.uomHeaderDimensions} />
            <Text element='div' textType={TextType.Label} value={messages.uomHeaderWeight} />
            <Text element='div' textType={TextType.Label} value={messages.uomHeaderStackable} />
            <div />
          </div>

          {baseRow && <BaseRowView row={baseRow} />}

          {isLoading && (
            <div className={style['uom-loading']}>
              <LoadingIndicator />
            </div>
          )}

          {orderedNonBase.map((row) => {
            const parent = parentForRow(row);
            const qtyUnitName = qtyUnitNameForParent(parent);
            return (
              <NonBaseRowView
                key={row.productUnitOfMeasureId}
                onDelete={() => openDeleteConfirm(row)}
                onEdit={() => openEditModal(row)}
                qtyUnitName={qtyUnitName}
                row={row}
              />
            );
          })}
        </div>

        {!isLoading && (
          <div className={style['add-unit-row']}>
            <Button onClick={openAddModal} theme={THEME_CONSTANTS.link}>
              <FlexRow className={style['icon-wrapper']}>
                <span>
                  <FontAwesomeIcon className={style['info-icon']} icon={faPlus} />
                </span>
                <Text className={style['info-text']} textType={TextType.Paragraph} value={messages.uomAddUnit} />
              </FlexRow>
            </Button>
          </div>
        )}

        {!isLoading && conversionSummary.length > 0 && (
          <div className={style['conversion-summary']}>
            <Text
              className={style['conversion-summary-title']}
              element='h4'
              textType={TextType.Label}
              value={messages.uomConversionSummaryTitle}
            />
            <ul className={style['conversion-list']}>
              {conversionSummary.map((line) => (
                <li key={line} className={style['conversion-item']}>
                  <Text element='span' textType={TextType.Caption} value={line} />
                </li>
              ))}
            </ul>
          </div>
        )}
      </div>

      {isModalOpen && (
        <UomModal
          dimensionUnitAbbr={dimensionUnit?.abbr}
          existingUnits={items}
          isEditing={editingRow !== null}
          onClose={closeModal}
          onSave={handleModalSave}
          productSku={productSku}
          row={editingRow}
          uomTypes={uomTypes}
          weightUnit={toWeightInputUnit(weightUnit)}
        />
      )}

      {deletingRow && (
        <SimpleModal
          actionButtonTheme={THEME_CONSTANTS.danger}
          actionMessage={messages.uomDeleteConfirmAction}
          cancelMessage={messages.uomDeleteConfirmCancel}
          headerMessage={messages.uomDeleteConfirmTitle}
          isActionDisabled={isDeleting}
          onAction={handleDelete}
          onCancel={closeDeleteConfirm}
          onClose={closeDeleteConfirm}
        >
          {isDeleting ? (
            <LoadingIndicator className={style['delete-loading']} />
          ) : (
            <Text element='div' textType={TextType.Paragraph} value={messages.uomDeleteConfirmBody} />
          )}
        </SimpleModal>
      )}

      {deleteBlockedInfo && (
        <SimpleModal
          cancelMessage={messages.uomDeleteBlockedDismiss}
          headerMessage={messages.uomDeleteBlockedTitle}
          onCancel={closeDeleteBlocked}
          onClose={closeDeleteBlocked}
        >
          <DeleteBlockedContent allItemsById={allItemsById} deleteBlockedInfo={deleteBlockedInfo} />
        </SimpleModal>
      )}
    </div>
  );
};

const formatDimensions = (row: ProductUnitOfMeasureResponse): string => {
  const { length, width, height } = row;
  if (length == null && width == null && height == null) return '-';
  const dims = [length ?? 0, width ?? 0, height ?? 0].join(' x ');
  const abbr = row.dimensionUnitId != null ? DIMENSION_ABBR_BY_ID.get(row.dimensionUnitId) : undefined;
  return abbr ? `${dims} (${abbr})` : dims;
};

const formatSplitWeight = (large: number, small: number, largeUnit: string, smallUnit: string): string => {
  if (large > 0 && small > 0) return `${large}${largeUnit} ${small}${smallUnit}`;
  if (large > 0) return `${large}${largeUnit}`;
  return `${small}${smallUnit}`;
};

const formatWeight = (row: ProductUnitOfMeasureResponse): string => {
  if (row.weight == null) return '-';
  const weight = row.weight;
  const unitId = row.weightUnitId;

  if (unitId === 1) {
    const { pounds, ounces } = getPoundsAndOuncesFromOunces(weight);
    return formatSplitWeight(pounds!, ounces!, 'lb', 'oz');
  }
  if (unitId === 2) {
    const { kilograms, grams } = getKilogramsAndGramsFromGrams(weight);
    return formatSplitWeight(kilograms!, grams!, 'kg', 'g');
  }

  const abbr = unitId != null ? WEIGHT_ABBR_BY_ID.get(unitId) : undefined;
  return abbr ? `${weight} ${abbr}` : String(weight);
};

type BaseRowViewProps = {
  row: ProductUnitOfMeasureResponse;
};

const BaseRowView = ({ row }: BaseRowViewProps) => (
  <div className={style['uom-row']}>
    <div className={style['cell-readonly']}>{row.uomTypeName}</div>
    <div className={style['cell-readonly']}>1</div>
    <div className={style['cell-readonly']}>{row.barcode ?? '-'}</div>
    <div className={style['cell-readonly']}>{row.uomSku ?? '-'}</div>
    <div className={style['cell-readonly']}>{formatDimensions(row)}</div>
    <div className={style['cell-readonly']}>{formatWeight(row)}</div>
    <div className={style['cell-readonly']}>
      <FormattedMessage {...(row.isStackable ? messages.uomStackableYes : messages.uomStackableNo)} />
    </div>
    <div className={style['cell-actions']} />
  </div>
);

type NonBaseRowViewProps = {
  row: ProductUnitOfMeasureResponse;
  qtyUnitName: string;
  onEdit: () => void;
  onDelete: () => void;
};

const NonBaseRowView = ({ row, qtyUnitName, onEdit, onDelete }: NonBaseRowViewProps) => {
  const quantityDisplay = qtyUnitName ? `${row.conversionFactor} x ${qtyUnitName}` : String(row.conversionFactor);

  return (
    <div className={style['uom-row']}>
      <div className={style['cell-readonly']}>{row.uomTypeName}</div>
      <div className={style['cell-readonly']}>{quantityDisplay}</div>
      <div className={style['cell-readonly']}>{row.barcode ?? '-'}</div>
      <div className={style['cell-readonly']}>{row.uomSku ?? '-'}</div>
      <div className={style['cell-readonly']}>{formatDimensions(row)}</div>
      <div className={style['cell-readonly']}>{formatWeight(row)}</div>
      <div className={style['cell-readonly']}>
        <FormattedMessage {...(row.isStackable ? messages.uomStackableYes : messages.uomStackableNo)} />
      </div>
      <div className={style['cell-actions']}>
        <DropdownMenu menuPosition='bottom-right'>
          <DropdownMenuToggler className={style['menu-toggle']} hideChevron openClassName={style['menu-toggle-open']} theme='link'>
            <div className={style['menu-toggle-contents']}>
              <FontAwesomeIcon icon={faEllipsisH} />
            </div>
          </DropdownMenuToggler>
          <DropdownMenuItem onClick={onEdit}>
            <FlexRow className={style['menu-item-row']}>
              <FontAwesomeIcon icon={faPen} />
              <Text textType={TextType.Paragraph} value={messages.uomRowEdit} />
            </FlexRow>
          </DropdownMenuItem>
          <DropdownMenuItem onClick={onDelete}>
            <FlexRow className={style['menu-item-row-delete']}>
              <FontAwesomeIcon icon={faTrash} />
              <Text textType={TextType.Paragraph} value={messages.uomRowDelete} />
            </FlexRow>
          </DropdownMenuItem>
        </DropdownMenu>
      </div>
    </div>
  );
};
